import { forwardRef, useMemo } from 'react';
import { DateTime } from 'luxon';
import { getFormatter } from 'data/services/date/formatter';
import { Calendar } from 'data/firestore/agent/calendar/types';
import {
    AllDayItems,
    Container,
    Hours,
    HeaderContent,
    HeaderItems,
    HeaderItem,
    HolidayChip,
} from '../CalendarDayView/header/styled';
import { DayNumber, HeaderLabel, WeekdayLabel } from './styled';
import getHolidaysForDate from '../CalendarDayView/header/getHolidaysForDate';

interface HeaderProps {
    days: Date[];
    collapsedDays?: boolean[];
    calendars: Calendar[];
    timeZone: string;
    locale?: string;
}

const Header = forwardRef<HTMLDivElement, HeaderProps>(
    (props, ref) => {
        const {
            days, collapsedDays = [], calendars, timeZone, locale = 'en-US',
        } = props;
        const todayKey = DateTime.now().setZone(timeZone).toFormat('yyyy-LL-dd');

        const dayHeaders = useMemo(
            () => days.map((day, index) => {
                const dayInTz = DateTime.fromJSDate(day).setZone(timeZone).setLocale(locale);
                const isToday = dayInTz.toFormat('yyyy-LL-dd') === todayKey;
                const holidays = getHolidaysForDate(calendars, day, timeZone);
                return (
                    <HeaderItem key={day.toISOString()} $collapsed={collapsedDays[index]}>
                        <HeaderContent>
                            <HeaderLabel>
                                <WeekdayLabel $isToday={isToday}>
                                    {getFormatter(locale, { weekday: 'short', timeZone }).format(dayInTz.toJSDate())}
                                </WeekdayLabel>
                                <DayNumber $isToday={isToday}>
                                    {getFormatter(locale, { day: 'numeric', timeZone }).format(dayInTz.toJSDate())}
                                </DayNumber>
                            </HeaderLabel>
                            <AllDayItems>
                                {holidays.map((holiday) => (
                                    <HolidayChip
                                        key={`${holiday.calendar.id}-${holiday.id}`}
                                        $color={holiday.calendar.color}
                                        title={`${holiday.name} — ${holiday.calendar.name}`}
                                    >
                                        {holiday.name}
                                    </HolidayChip>
                                ))}
                            </AllDayItems>
                        </HeaderContent>
                    </HeaderItem>
                );
            }),
            [calendars, collapsedDays, days, locale, timeZone, todayKey],
        );

        return (
            <Container role="presentation">
                <Hours />
                <HeaderItems ref={ref}>
                    <div />
                    {dayHeaders}
                </HeaderItems>
                <div />
            </Container>
        );
    },
);

export default Header;
